/**
 * Utility to extract track changes metadata from Word OOXML
 * Used to detect redlines and identify the last change author
 */

export interface TrackChange {
  type: 'insertion' | 'deletion';
  author: string;
  timestamp: string; // ISO format for easy comparison
  raw_timestamp: string; // Original Word timestamp
}

export class TrackChangesExtractor {
  private static readonly W_NAMESPACE = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  /**
   * Extract all track changes (insertions and deletions) from OOXML
   * @param ooxml - Word OOXML string
   * @returns Array of track changes sorted by timestamp (oldest first)
   */
  static extractTrackChanges(ooxml: string): TrackChange[] {
    if (!ooxml || ooxml.trim().length === 0) {
      return [];
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ooxml, 'text/xml');

      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.warn('[TrackChangesExtractor] XML parsing error:', parserError.textContent);
        return [];
      }

      const changes: TrackChange[] = [];

      // Find all insertion elements (w:ins) - check at paragraph level
      const insertions = this.findElementsByTagName(xmlDoc, 'ins');
      
      // Also check at run level (w:r)
      const runs = this.findElementsByTagName(xmlDoc, 'r');
      
      // Add run-level insertions to the list
      for (const run of runs) {
        const runIns = this.findElementsByTagName(run as any, 'ins');
        insertions.push(...runIns);
      }
      
      for (const ins of insertions) {
        const author = this.getAttributeValue(ins, 'author') || 'Unknown';
        const rawTimestamp = this.getAttributeValue(ins, 'date') || '';
        const timestamp = this.parseWordTimestamp(rawTimestamp);

        changes.push({
          type: 'insertion',
          author,
          timestamp,
          raw_timestamp: rawTimestamp,
        });
      }

      // Find all deletion elements (w:del) - check at paragraph level
      const deletions = this.findElementsByTagName(xmlDoc, 'del');
      
      // Add run-level deletions to the list
      for (const run of runs) {
        const runDel = this.findElementsByTagName(run as any, 'del');
        deletions.push(...runDel);
      }
      
      for (const del of deletions) {
        const author = this.getAttributeValue(del, 'author') || 'Unknown';
        const rawTimestamp = this.getAttributeValue(del, 'date') || '';
        const timestamp = this.parseWordTimestamp(rawTimestamp);

        changes.push({
          type: 'deletion',
          author,
          timestamp,
          raw_timestamp: rawTimestamp,
        });
      }

      // Sort by timestamp (oldest first)
      changes.sort((a, b) => {
        if (!a.timestamp || !b.timestamp) {
          return 0;
        }
        return a.timestamp.localeCompare(b.timestamp);
      });

      return changes;
    } catch (error) {
      console.error('[TrackChangesExtractor] Error extracting track changes:', error);
      return [];
    }
  }

  /**
   * Get the most recent track change by timestamp
   * @param ooxml - Word OOXML string
   * @returns The last track change, or null if none found
   */
  static getLastTrackChange(ooxml: string): TrackChange | null {
    const changes = this.extractTrackChanges(ooxml);
    if (changes.length === 0) {
      return null;
    }

    // Return the last change (most recent)
    return changes[changes.length - 1];
  }

  /**
   * Check if OOXML contains any track changes
   * @param ooxml - Word OOXML string
   * @returns True if track changes exist, false otherwise
   */
  static hasTrackChanges(ooxml: string): boolean {
    if (!ooxml || ooxml.trim().length === 0) {
      return false;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ooxml, 'text/xml');

      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return false;
      }

      // Quick check for w:ins or w:del elements
      const hasIns = this.findElementsByTagName(xmlDoc, 'ins').length > 0;
      const hasDel = this.findElementsByTagName(xmlDoc, 'del').length > 0;

      return hasIns || hasDel;
    } catch (error) {
      console.error('[TrackChangesExtractor] Error checking track changes:', error);
      return false;
    }
  }

  /**
   * Find elements by tag name, handling both namespaced and non-namespaced forms
   * @param xmlDoc - Parsed XML document
   * @param tagName - Local tag name (without namespace prefix)
   * @returns Array of matching elements
   */
  private static findElementsByTagName(xmlDoc: Document | Element, tagName: string): Element[] {
    const elements: Element[] = [];

    // Try with w: prefix
    const withPrefix = xmlDoc.getElementsByTagName(`w:${tagName}`);
    for (let i = 0; i < withPrefix.length; i++) {
      elements.push(withPrefix[i]);
    }

    // Try without prefix (in case namespace is different)
    const withoutPrefix = xmlDoc.getElementsByTagName(tagName);
    for (let i = 0; i < withoutPrefix.length; i++) {
      // Avoid duplicates
      if (!elements.includes(withoutPrefix[i])) {
        elements.push(withoutPrefix[i]);
      }
    }

    // Try with namespace using getElementsByTagNameNS
    const withNamespace = xmlDoc.getElementsByTagNameNS(this.W_NAMESPACE, tagName);
    for (let i = 0; i < withNamespace.length; i++) {
      // Avoid duplicates
      if (!elements.includes(withNamespace[i])) {
        elements.push(withNamespace[i]);
      }
    }

    return elements;
  }

  /**
   * Get attribute value from element, trying different namespace variations
   * @param element - XML element
   * @param attrName - Attribute name (without namespace prefix)
   * @returns Attribute value or null
   */
  private static getAttributeValue(element: Element, attrName: string): string | null {
    // Try with w: prefix
    let value = element.getAttribute(`w:${attrName}`);
    if (value) return value;

    // Try without prefix
    value = element.getAttribute(attrName);
    if (value) return value;

    // Try with namespace
    value = element.getAttributeNS(this.W_NAMESPACE, attrName);
    if (value) return value;

    return null;
  }

  /**
   * Parse Word timestamp to ISO format
   * Word timestamps are usually in ISO 8601 format already (e.g., "2024-01-15T10:30:00Z")
   * @param wordTimestamp - Word timestamp string
   * @returns ISO timestamp string
   */
  private static parseWordTimestamp(wordTimestamp: string): string {
    if (!wordTimestamp) {
      return '';
    }

    try {
      // Word timestamps are typically ISO 8601 format
      // Just validate and return as-is
      const date = new Date(wordTimestamp);
      if (isNaN(date.getTime())) {
        console.warn('[TrackChangesExtractor] Invalid timestamp:', wordTimestamp);
        return wordTimestamp; // Return as-is if invalid
      }
      return date.toISOString();
    } catch (error) {
      console.warn('[TrackChangesExtractor] Error parsing timestamp:', wordTimestamp, error);
      return wordTimestamp; // Return as-is if parsing fails
    }
  }
}

